import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface CablesBatimentsCategoryProps {
  t: any;
  isRTL: boolean;
}

const CategoryPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow-x: hidden;
  width: 100%;
  padding-top: 120px;
  
  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0 1.5rem;
  font-size: 0.9rem;
  color: #64748b;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    font-size: 0.8rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isRTL: boolean }>`
  color: #64748b;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: #dc2626;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #cbd5e1;
  margin: 0 0.25rem;
`;

const BreadcrumbCurrent = styled.span<{ $isRTL: boolean }>`
  color: #1e293b;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const CategoryTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const FilterSection = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FilterButton = styled.button<{ $isRTL: boolean; $active: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.$active ? '#dc2626' : '#e2e8f0'};
  background: ${props => props.$active ? '#dc2626' : 'white'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    border-color: #dc2626;
    background: ${props => props.$active ? '#dc2626' : '#fef2f2'};
    color: ${props => props.$active ? 'white' : '#dc2626'};
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
  }
`;

const ProductsCount = styled.p<{ $isRTL: boolean }>`
  text-align: center;
  color: #64748b;
  margin-bottom: 2rem;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const ProductCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const ProductImage = styled.div`
  height: 200px;
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
  
  @media (max-width: 768px) {
    height: 180px;
    padding: 0.75rem;
  }
`;

const ProductContent = styled.div<{ $isRTL: boolean }>`
  padding: 1.25rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ProductCategory = styled.span<{ $isRTL: boolean }>`
  display: inline-block;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
    margin-bottom: 0.5rem;
  }
`;

const ProductTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.4rem;
  }
`;

const CablesBatimentsCategory: React.FC<CablesBatimentsCategoryProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = React.useState('1kv');

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/catalogue" },
    { label: t.cableBuildingsTitle, path: "/category/cables-batiments" },
  ];

  const filterCategories = [
    { id: '1kv', label: t.standardCables1kvTitle, hasDropdown: false },
    { id: 'lt1kv', label: t.standardCablesLt1kvTitle, hasDropdown: false },
  ];

  const products = [
    {
      id: 'u-1000-ar2v',
      title: 'U-1000 AR2V (industriels non armés)',
      category: 'FILS ET CÂBLES STANDARD 1KV',
      image: '/images/catalog/Câbles de bâtiments/U-1000 AR2V (industriels non armés).jpg',
      filter: '1kv'
    },
    {
      id: 'u-1000-arvfv',
      title: 'U-1000 ARVFV (industriel armé)',
      category: 'FILS ET CÂBLES STANDARD 1KV',
      image: '/images/catalog/Câbles de bâtiments/U-1000 ARVFV (industriel armé).jpg',
      filter: '1kv'
    },
    {
      id: 'u-1000-r2v',
      title: 'U-1000 R2V (industriel non armé)',
      category: 'FILS ET CÂBLES STANDARD 1KV',
      image: '/images/catalog/Câbles de bâtiments/U-1000 R2V (industriel non armé).jpg',
      filter: '1kv'
    },
    {
      id: '0-6-1k-rv-k',
      title: '0.6/1K RV-K',
      category: 'FILS ET CÂBLES STANDARD 1KV',
      image: '/images/catalog/Câbles de bâtiments/0.61K RV-K.jpg',
      filter: '1kv'
    },
    {
      id: 'u-1000-rvfv',
      title: 'U-1000 RVFV (industriels armés)',
      category: 'FILS ET CÂBLES STANDARD 1KV',
      image: '/images/catalog/Câbles de bâtiments/U-1000 RVFV (industriels armés).jpg',
      filter: '1kv'
    },
    {
      id: 'h05w-f',
      title: 'H05W-F (U500 SV1V)',
      category: 'FILS ET CÂBLES STANDARD <1KV',
      image: '/images/catalog/Câbles de bâtiments/H05VV-F (U500 SV1V).jpg',
      filter: 'lt1kv'
    },
    {
      id: 'u500v-h07v',
      title: 'U500V : H07V-U Filvit / H07V-R',
      category: 'FILS ET CÂBLES STANDARD <1KV',
      image: '/images/catalog/Câbles de bâtiments/U500V  H07V-U Filvit  H07V-R.jpg',
      filter: 'lt1kv'
    },
    {
      id: 'u500-sv-h07v-k',
      title: 'U500 SV (H07 V-K)',
      category: 'FILS ET CÂBLES STANDARD <1KV',
      image: '/images/catalog/Câbles de bâtiments/U500 SV (H07 V-K).jpg',
      filter: 'lt1kv'
    }
  ];

  const filteredProducts = products.filter(product => product.filter === activeFilter);

  return (
    <CategoryPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs 
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
              {index === breadcrumbs.length - 1 ? (
                <BreadcrumbCurrent $isRTL={isRTL}>{breadcrumb.label}</BreadcrumbCurrent>
              ) : (
                <BreadcrumbLink 
                  $isRTL={isRTL}
                  onClick={() => navigate(breadcrumb.path)}
                >
                  {breadcrumb.label}
                </BreadcrumbLink>
              )}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <CategoryTitle 
          $isRTL={isRTL}
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
{t.cableBuildingsTitle}
        </CategoryTitle>

        <FilterSection $isRTL={isRTL}>
          {filterCategories.map((filter) => (
            <FilterButton 
              key={filter.id}
              $isRTL={isRTL}
              $active={activeFilter === filter.id}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </FilterSection>

        <ProductsCount $isRTL={isRTL}>
          {filteredProducts.length} categories
        </ProductsCount>

        <ProductsGrid>
          {filteredProducts.map((product, index) => (
            <ProductCard
              key={product.id}
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <ProductImage>
                <img
                  src={product.image}
                  alt={product.title}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/products/default-product.jpg';
                  }}
                />
              </ProductImage>
              <ProductContent $isRTL={isRTL}>
                <ProductCategory $isRTL={isRTL}>
                  {product.category}
                </ProductCategory>
                <ProductTitle $isRTL={isRTL}>
                  {product.title}
                </ProductTitle>
              </ProductContent>
            </ProductCard>
          ))}
        </ProductsGrid>
      </Container>
    </CategoryPage>
  );
};

export default CablesBatimentsCategory;
